// Contribution from Mercatura DeFi Team to Menese Protocol
// (2-of-2 Threshold ECDSA — Tier 7 §3.E Internet Identity).
// Mercatura Forum proprietary.
//
// Thin typed wrapper around `@dfinity/auth-client`. Wraps the II
// login flow into a single async API and surfaces the
// `DelegationIdentity` for the §3.F `ic-agent` layer.
//
// Anti-stub posture (Tier-7 §1.B): the agent constructor in `agent.ts`
// REFUSES anonymous identity in production builds. This module is
// the only sanctioned path to a non-anonymous identity.

import { AuthClient } from "@dfinity/auth-client";
import type { Identity, SignIdentity } from "@dfinity/agent";
import type { Principal } from "@dfinity/principal";

// Tier-7 §3.I test override. Playwright's `addInitScript` may set a
// pre-baked `SignIdentity` on `window.__MENESE_TEST_IDENTITY__` before
// the app loads; if present, `IiAuth` short-circuits the live II flow
// and uses that identity directly. The hatch is gated on the global
// being a function-shaped object that returns an `Identity` — random
// JS pages cannot accidentally trip it. Production builds pass through
// to live II.
declare global {
  interface Window {
    __MENESE_TEST_IDENTITY__?: () => SignIdentity;
  }
}

// Internet Identity production URL. `id.ai` is the new Dfinity-branded
// front-end for the same II canister (`rdmx6-jaaaa-aaaaa-aaadq-cai`).
// Both `id.ai` and `identity.ic0.app` resolve to that canister; we use
// `id.ai` for cleaner branding and shorter URLs in the user-facing flow.
const II_URL_PROD = "https://id.ai";
const II_URL_LOCAL = "http://identity.localhost:4945";

export interface IiAuthOptions {
  /** Override the II provider URL. Defaults to the production II
   *  on `--network ic` and the local II on `--network local` (the
   *  `network` field is a Vite environment variable evaluated at
   *  build time; production builds default to `ic`). */
  iiUrl?: string;
}

export interface IiSession {
  principal: Principal;
  identity: Identity;
  logout: () => Promise<void>;
}

export class IiAuth {
  private client: AuthClient | null = null;
  private readonly iiUrl: string;

  constructor(opts: IiAuthOptions = {}) {
    this.iiUrl =
      opts.iiUrl ??
      (import.meta.env.PROD ? II_URL_PROD : II_URL_LOCAL);
  }

  /** Lazy-create the underlying `AuthClient` so this module is
   * importable in environments without `window` (e.g. `tsc --noEmit`). */
  private async ensureClient(): Promise<AuthClient> {
    if (!this.client) {
      this.client = await AuthClient.create({
        idleOptions: { disableIdle: true },
      });
    }
    return this.client;
  }

  /** Trigger the II popup login. Resolves once the user authorizes the
   * delegation. */
  async login(): Promise<IiSession> {
    const testIdentity = this.maybeTestIdentity();
    if (testIdentity) return testIdentity;
    const client = await this.ensureClient();
    return new Promise<IiSession>((resolve, reject) => {
      client.login({
        identityProvider: this.iiUrl,
        maxTimeToLive: BigInt(8 * 3600 * 1_000_000_000), // 8 h
        onSuccess: () => {
          const identity = client.getIdentity();
          resolve({
            principal: identity.getPrincipal(),
            identity,
            logout: async () => {
              await client.logout({ returnTo: window.location.origin });
            },
          });
        },
        onError: (err) => reject(new Error(`II login failed: ${err}`)),
      });
    });
  }

  /** Returns the current session if the user is already
   * authenticated (e.g. on page reload). Returns `null` otherwise. */
  async getCurrentSession(): Promise<IiSession | null> {
    const testIdentity = this.maybeTestIdentity();
    if (testIdentity) return testIdentity;
    const client = await this.ensureClient();
    if (!(await client.isAuthenticated())) return null;
    const identity = client.getIdentity();
    if (identity.getPrincipal().isAnonymous()) return null;
    return {
      principal: identity.getPrincipal(),
      identity,
      logout: async () => {
        await client.logout({ returnTo: window.location.origin });
      },
    };
  }

  private maybeTestIdentity(): IiSession | null {
    if (typeof window === "undefined") return null;
    const factory = window.__MENESE_TEST_IDENTITY__;
    if (typeof factory !== "function") return null;
    const identity = factory();
    return {
      principal: identity.getPrincipal(),
      identity,
      logout: async () => {
        delete window.__MENESE_TEST_IDENTITY__;
      },
    };
  }
}
